import { inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { getFromStorage, logoutRedirect, saveToStorage } from '../constants/auth-state';
import { globalConst } from '../constants/global.constants';
import { Shared } from './shared';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private readonly INACTIVITY_TIMEOUT = (globalConst.inactivityInMins || 30) * 60 * 1000; // 30 minutes in milliseconds
  private checkInterval?: any;
  private lastLocalStorageWrite = 0;

  private readonly shared = inject(Shared);
  private readonly ngZone = inject(NgZone);
  private readonly appRouter = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly authService = inject(AuthService);

  private readonly activityEvents = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click'
  ];

  startWatching(): void {
    // Perform an immediate check on startup
    if (this.checkInactivity()) {
      return;
    }

    this.setupActivityListeners();
    this.setupStorageAndVisibilityListeners();

    // Set initial activity time if not set
    if (!getFromStorage('lastActivityTime')) {
      saveToStorage('lastActivityTime', Date.now().toString());
    }

    // Start periodic check every 5 seconds
    this.ngZone.runOutsideAngular(() => {
      this.checkInterval = setInterval(() => {
        this.ngZone.run(() => {
          this.checkInactivity();
        });
      }, 5000);
    });
  }

  stopWatching(): void {
    this.removeActivityListeners();
    this.removeStorageAndVisibilityListeners();
    this.clearCheckInterval();
  }

  private setupActivityListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      this.activityEvents.forEach(event => {
        window.addEventListener(event, this.onUserActivity, true);
      });
    });
  }

  private removeActivityListeners(): void {
    this.activityEvents.forEach(event => {
      window.removeEventListener(event, this.onUserActivity, true);
    });
  }

  private onUserActivity = (): void => {
    const now = Date.now();
    // Throttle writes to localStorage to at most once every 10 seconds
    if (now - this.lastLocalStorageWrite > 10000) {
      this.lastLocalStorageWrite = now;
      saveToStorage('lastActivityTime', now.toString());
    }
  };

  private setupStorageAndVisibilityListeners(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onStorageChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  private removeStorageAndVisibilityListeners(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.onStorageChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  private onStorageChange = (event: StorageEvent): void => {
    if (event.key === 'lastActivityTime') {
      this.checkInactivity();
    }
  };

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.checkInactivity();
    }
  };

  private clearCheckInterval(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = undefined;
    }
  }

  /**
   * Checks the inactivity time.
   * Returns true if logged out, false otherwise.
   */
  private checkInactivity(): boolean {
    const lastActivityStr = getFromStorage('lastActivityTime');
    if (!lastActivityStr) return false;

    const lastActivity = Number.parseInt(lastActivityStr, 10);
    if (Number.isNaN(lastActivity)) return false;

    const elapsed = Date.now() - lastActivity;
    if (elapsed >= this.INACTIVITY_TIMEOUT) {
      void this.logout();
      return true;
    }
    return false;
  }

  private async logout(): Promise<void> {
    this.stopWatching();
    saveToStorage('lastActivityTime', null);

    await this.shared.runAutoSaveHandlers();

    this.shared.setIntentionalLogout(true);
    this.dialog.closeAll();
    void this.authService.logout().catch((error: unknown) => {
      console.error('Provider logout failed during inactivity timeout. Falling back to legacy redirect.', error);
      logoutRedirect(this.shared, this.appRouter, this.dialog);
    });
  }
}
